import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Grid,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  SelectChangeEvent,
  Card,
  CardContent,
  CardActions,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Restore as RestoreIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface ContactBusiness {
  businessId: string;
  businessName: string;
  businessContactType?: string;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  dateCreated: string;
  archivedDate?: string;
  businesses?: ContactBusiness[];
}

interface Business {
  id: string;
  name: string;
  abn: string;
}

interface BusinessAssociation {
  businessId: string;
  businessContactType: string;
}

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  businessAssociations: BusinessAssociation[];
}

const ContactManagement: React.FC = () => {
  const auth = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessAssociations: [],
  });

  const fetchBusinesses = async () => {
    try {
      if (!auth.isSignedIn || !auth.tokens?.accessToken) {
        return;
      }

      const response = await fetch('/api/businesses/list', {
        headers: {
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBusinesses(data);
      }
    } catch (err) {
      console.error('Error fetching businesses:', err);
    }
  };

  const fetchContacts = async () => {
    try {
      setLoading(true);
      
      if (!auth.isSignedIn || !auth.tokens?.accessToken) {
        setError('Not authenticated. Please log in.');
        setLoading(false);
        return;
      }
      
      const url = `/api/contacts${includeArchived ? '?includeArchived=true' : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${auth.tokens?.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch contacts: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setContacts(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.isSignedIn && auth.tokens?.accessToken) {
      fetchContacts();
      fetchBusinesses();
    }
  }, [includeArchived, auth.isSignedIn]);

  const handleOpenDialog = (contact?: Contact) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        phone: contact.phone || '',
        businessAssociations: contact.businesses?.map(b => ({
          businessId: b.businessId,
          businessContactType: b.businessContactType || 'Primary'
        })) || [],
      });
    } else {
      setEditingContact(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        businessAssociations: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingContact(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      businessAssociations: [],
    });
  };

  const handleSubmit = async () => {
    try {
      const url = editingContact
        ? `/api/contacts/${editingContact.id}`
        : '/api/contacts';

      const method = editingContact ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${auth.tokens?.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || `Failed to ${editingContact ? 'update' : 'create'} contact`);
      }

      await fetchContacts();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving contact:', err);
      setError(err instanceof Error ? err.message : 'Failed to save contact');
    }
  };

  const handleArchive = async (id: string) => {
    if (!window.confirm('Are you sure you want to archive this contact?')) {
      return;
    }

    try {
      const response = await fetch(`/api/contacts/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to archive contact');
      }

      await fetchContacts();
    } catch (err) {
      console.error('Error archiving contact:', err);
      setError(err instanceof Error ? err.message : 'Failed to archive contact');
    }
  };

  const handleRestore = async (id: string) => {
    try {
      const response = await fetch(`/api/contacts/${id}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || 'Failed to restore contact');
      }

      await fetchContacts();
    } catch (err) {
      console.error('Error restoring contact:', err);
      setError(err instanceof Error ? err.message : 'Failed to restore contact');
    }
  };

  const handleInputChange = (field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddBusiness = () => {
    setFormData((prev) => ({
      ...prev,
      businessAssociations: [
        ...prev.businessAssociations,
        { businessId: '', businessContactType: 'Primary' },
      ],
    }));
  };

  const handleRemoveBusiness = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      businessAssociations: prev.businessAssociations.filter((_, i) => i !== index),
    }));
  };

  const handleBusinessChange = (index: number, field: 'businessId' | 'businessContactType', value: string) => {
    setFormData((prev) => ({
      ...prev,
      businessAssociations: prev.businessAssociations.map((assoc, i) =>
        i === index ? { ...assoc, [field]: value } : assoc
      ),
    }));
  };

  if (loading && contacts.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={1.5}
        mb={2}
      >
        <Typography variant="h6">
          <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Contact Management
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
              />
            }
            label="Show Archived"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Contact
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {contacts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No contacts found</Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {contacts.map((contact) => (
            <Card key={contact.id} variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {contact.firstName} {contact.lastName}
                  </Typography>
                  {contact.archivedDate ? (
                    <Chip label="Archived" color="error" size="small" />
                  ) : (
                    <Chip label="Active" color="success" size="small" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">{contact.email}</Typography>
                {contact.phone && (
                  <Typography variant="body2" color="text.secondary">{contact.phone}</Typography>
                )}
                {contact.businesses && contact.businesses.length > 0 && (
                  <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
                    {contact.businesses.map((business, idx) => (
                      <Tooltip key={idx} title={business.businessContactType || 'Contact'}>
                        <Chip icon={<BusinessIcon />} label={business.businessName} size="small" variant="outlined" />
                      </Tooltip>
                    ))}
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Created {new Date(contact.dateCreated).toLocaleDateString()}
                </Typography>
              </CardContent>
              <CardActions>
                {contact.archivedDate ? (
                  <Button size="small" startIcon={<RestoreIcon fontSize="small" />} onClick={() => handleRestore(contact.id)}>
                    Restore
                  </Button>
                ) : (
                  <>
                    <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => handleOpenDialog(contact)}>
                      Edit
                    </Button>
                    <Button size="small" color="error" startIcon={<DeleteIcon fontSize="small" />} onClick={() => handleArchive(contact.id)}>
                      Archive
                    </Button>
                  </>
                )}
              </CardActions>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ '& .MuiTableCell-root': { py: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Businesses</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell>
                    {contact.firstName} {contact.lastName}
                  </TableCell>
                  <TableCell>{contact.email}</TableCell>
                  <TableCell>{contact.phone || '-'}</TableCell>
                  <TableCell>
                    {contact.businesses && contact.businesses.length > 0 ? (
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {contact.businesses.map((business, idx) => (
                          <Tooltip
                            key={idx}
                            title={business.businessContactType || 'Contact'}
                          >
                            <Chip
                              icon={<BusinessIcon />}
                              label={business.businessName}
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {contact.archivedDate ? (
                      <Chip label="Archived" color="error" size="small" />
                    ) : (
                      <Chip label="Active" color="success" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(contact.dateCreated).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    {contact.archivedDate ? (
                      <Tooltip title="Restore Contact">
                        <IconButton
                          onClick={() => handleRestore(contact.id)}
                          color="primary"
                          size="small"
                        >
                          <RestoreIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <>
                        <Tooltip title="Edit Contact">
                          <IconButton
                            onClick={() => handleOpenDialog(contact)}
                            color="primary"
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Archive Contact">
                          <IconButton
                            onClick={() => handleArchive(contact.id)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingContact ? 'Edit Contact' : 'Add New Contact'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Business Associations
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddBusiness}
                  variant="outlined"
                >
                  Add Business
                </Button>
              </Box>
            </Grid>

            {formData.businessAssociations.length === 0 ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" align="center" py={2}>
                  No business associations. Click "Add Business" to link this contact to a business.
                </Typography>
              </Grid>
            ) : (
              formData.businessAssociations.map((assoc, index) => (
                <Grid item xs={12} key={index}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required>
                          <InputLabel>Business</InputLabel>
                          <Select
                            value={assoc.businessId}
                            onChange={(e) => handleBusinessChange(index, 'businessId', e.target.value)}
                            input={<OutlinedInput label="Business" />}
                          >
                            <MenuItem value="">
                              <em>Select a business</em>
                            </MenuItem>
                            {businesses.map((business) => (
                              <MenuItem key={business.id} value={business.id}>
                                {business.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <FormControl fullWidth>
                          <InputLabel>Contact Type</InputLabel>
                          <Select
                            value={assoc.businessContactType}
                            onChange={(e) => handleBusinessChange(index, 'businessContactType', e.target.value)}
                            input={<OutlinedInput label="Contact Type" />}
                          >
                            <MenuItem value="Primary">Primary</MenuItem>
                            <MenuItem value="Billing">Billing</MenuItem>
                            <MenuItem value="Technical">Technical</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={1}>
                        <IconButton
                          onClick={() => handleRemoveBusiness(index)}
                          color="error"
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              ))
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formData.firstName || !formData.lastName || !formData.email}
          >
            {editingContact ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactManagement;
